'use client'

import { usePathname } from 'next/navigation'

import _isEmpty from 'lodash/isEmpty'

import { CHECKOUT_SLUG, INDEX_SLUG, LOGIN_SLUG } from '@/app/constants'
import useGlammaticApi from '@/hooks/use-glammatic-api'
import useStorage from '@/hooks/use-storage'
import { getFromStorage } from '@/hooks/use-storage/use-storage'
import { USER_AUTH_STORAGE_KEY } from '@/hooks/use-storage/use-storage.constants'
import useVisibleInterval from '@/hooks/use-visible-interval'
import { Logger } from '@/lib/logger/log'
import { isUserSessionValid } from '@/lib/user/auth'

const EVERY_2_MINUTES = 1000 * 60 * 2

const DISABLED_ROUTES = [LOGIN_SLUG, CHECKOUT_SLUG]

export default function TokenValidator() {
  const pathname = usePathname()
  const { value: user, storageLoaded } = useStorage<UserAuth>(USER_AUTH_STORAGE_KEY)
  const { loaded: apiLoaded, logout, me } = useGlammaticApi()

  const isDisabledRoute =
    !pathname ||
    pathname === INDEX_SLUG ||
    DISABLED_ROUTES.some(route => pathname.startsWith(route))
  const enabled = apiLoaded && storageLoaded && !isDisabledRoute

  async function runAuthChecks() {
    if (!enabled) {
      return
    }

    const finalUser = user || getFromStorage<UserAuth>(USER_AUTH_STORAGE_KEY)

    // 1) Local/session check: quickest path to logout
    if (_isEmpty(finalUser) || !isUserSessionValid(finalUser)) {
      Logger.info('User session expired, session is invalid in storage')
      await logout()
      return
    }

    // 2) Server-side validation (may refresh/401/403 -> logout via your API layer)
    try {
      await me({ forceDataRefresh: true, reportToSentry: false })
    } catch {
      Logger.info('User session expired, token is not valid anymore')
      // pass
    }
  }

  useVisibleInterval(runAuthChecks, {
    interval: EVERY_2_MINUTES,
    enabled,
  })

  return null
}
